import React, { useCallback, useMemo } from 'react'
import { useModal } from '../../../components/useModal'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import TextInput from '../../../components/FormElements/TextInput'
import { Button } from '@demandlocal/components'
import { isEmpty, omit } from 'lodash'
import { toast } from 'react-hot-toast'
import { UseMutationResult } from 'react-query'
import { useInView } from 'react-intersection-observer'
import { motion } from 'framer-motion'
import ColumnsMapper from '../../../components/CustomersUpload/ColumnsMapper'
import {
  customersMapperMongoColumns,
  hashedCustomersMapperMongoColumns,
  optOutCustomersMapperMongoColumns,
} from '../../../lib/utils/customersMapperMongoColumns'
import { useDealership } from '../../../store/zustand/dealershipStore'
import { match } from 'ts-pattern'
import { useCreateManualUploadSource } from '../../../api/dealership/customers/useCreateManualUploadSource'
import { queryClient } from '../../../api/queryClient'

type CSVData = {
  data: Array<Record<string, unknown>>
}

type Props = {
  data: CSVData
  manualUploadCSVMutation: UseMutationResult<
    unknown,
    unknown,
    { file: File; mapped_columns: unknown; customer_source_id: string },
    unknown
  >
  databaseColumns?: Array<string>
  onUploadComplete?: () => void
  customer_source_id?: number
  selectedUploadType:
    | 'sales'
    | 'customers'
    | 'leads'
    | 'service'
    | 'other'
    | 'hashed-customers'
    | 'opt-out-customers'
  file: File
}

const ManualUploadMapper = (props: Props): JSX.Element => {
  const {
    data,
    file,
    manualUploadCSVMutation,
    onUploadComplete,
    selectedUploadType,
    customer_source_id,
  } = props
  const { closeModal } = useModal()
  const dealership = useDealership()

  const { mutateAsync: createManualUploadSource } = useCreateManualUploadSource()
  const [ref, inView] = useInView({
    threshold: 1,
    triggerOnce: true,
    fallbackInView: true,
  })
  const formContext = useForm({
    mode: 'onSubmit',
  })
  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting, errors },
  } = formContext
  const columns = useMemo(() => {
    return Object.keys(data.data[0])
      .filter((key) => key !== 'id')
      .filter((key) => !isEmpty(key))
      .map((key) => {
        return {
          accessor: key,
          Header: key,
          columns: [{ accessor: key, Header: key }],
        }
      })
  }, [data])

  const isEmailMapped = useCallback((formValues) => {
    return Boolean(
      Object.entries(formValues)
        .filter(([key, value]) => value)
        // @ts-ignore
        .map(([key, value]) => value?.value)
        .filter((value) => ['email', 'email_sha_2_hash'].includes(value)).length,
    )
  }, [])

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const toastId = toast.loading(
      'Uploading customers... Please do not close this window.',
    )
    const sourceName = values.source_name
    if (
      !isEmailMapped(values) &&
      selectedUploadType !== 'hashed-customers' &&
      selectedUploadType !== 'opt-out-customers'
    ) {
      setError('email', { type: 'custom', message: '' })
      return
    }
    const rows = omit(values, 'source_name')
    const r = Object.entries(rows)
      .filter(([key, value]) => value)
      .map(([key, value]) => {
        return { [key]: value.value }
      })
    const response = customer_source_id
      ? customer_source_id
      : await createManualUploadSource({
          data: {
            sourceName,
            sourceType: selectedUploadType,
          },
        }).then((res) => res.id)
    manualUploadCSVMutation.mutate(
      {
        mapped_columns: r,
        file,
        customer_source_id: response,
      },
      {
        onSuccess: () => {
          closeModal()
          toast.success('Customers successfully uploaded!', {
            id: toastId,
          })
          queryClient.refetchQueries(['dealership', dealership?.id, 'customers'])
          queryClient.refetchQueries(['dealership', dealership?.id, 'sold_reports'])
          queryClient.refetchQueries(['dealership', dealership?.id, 'stats'])
          onUploadComplete?.()
        },
        onError: () => {
          toast.error('Something went wrong', {
            id: toastId,
          })
        },
      },
    )
  }

  const rowsOptions = match(selectedUploadType)
    .with('hashed-customers', () => hashedCustomersMapperMongoColumns)
    .with('opt-out-customers', () => optOutCustomersMapperMongoColumns)
    .otherwise(() => customersMapperMongoColumns)

  return (
    <FormProvider {...formContext}>
      <form
        ref={ref}
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col pt-12 font-Spartan'}>
        <div className={'flex flex-1 flex-col px-12'}>
          <div className={'font-bold text-[28px] text-primary-xiketic'}>
            Import CSV data
          </div>
          {customer_source_id ? (
            <></>
          ) : (
            <div className={'pt-8 pb-8 border-b-2'}>
              <div className={'w-1/2'}>
                <TextInput
                  rules={{ required: 'This field is required' }}
                  inverted
                  placeholder={'Ex. CRM-download-import_Mar2022'}
                  name={'source_name'}
                  label={'Name your source'}
                  control={control}
                  info={'Give it a clear name so you remember it easily'}
                />
              </div>
            </div>
          )}
          <div className={'flex flex-1 flex-col relative pt-8'}>
            <div className={'flex flex-row justify-between items-center pb-3'}>
              <div className={'flex flex-col'}>
                <span className={'font-bold text-base'}>Match rows</span>
                <span className={'mt-2 font-medium text-sm'}>
                  For a precise upload, please match the table headers below
                  {data.data?.length > 200 && (
                    <span className={'ml-2 font-normal text-xs'}>
                      (Showing first 200 rows)
                    </span>
                  )}
                </span>
              </div>
            </div>
            {errors.email && (
              <div className={'pb-3 font-medium text-sm text-secondary-bittersweet-red'}>
                Mapping email address is required.
              </div>
            )}
            <div className={'absolute top-0 left-0 w-full h-full overflow-scroll'}>
              <ColumnsMapper
                data={data.data}
                columns={columns}
                rowsOptions={rowsOptions}
              />
            </div>
            {!inView && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={'flex flex-1 items-center justify-center'}>
                Loading...
              </motion.div>
            )}
          </div>
        </div>
        <div className={'px-12'}>
          <div className={'items-center flex justify-between border-t-2 py-4'}>
            <Button
              variant={'secondary'}
              disabled={isSubmitting}
              onClick={(e) => {
                e.preventDefault()
                closeModal()
              }}>
              Close
            </Button>
            <Button disabled={isSubmitting} type={'submit'}>
              Import
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default ManualUploadMapper
